import type { QuoteItem } from '@techgitter/contracts';
import { optionLineSellDisplay, optionQty } from './quotePricing';

/**
 * The New Desktop (Custom) build sheet's internal money summary: what the
 * customer pays for the parts, what the parts cost us, the freight, and what
 * is left over. Internal only — buy price and shipping never reach the
 * customer preview or the invoice.
 *
 * Only lines that bill count (marked, with a selected option — the same rule
 * as promotion), and **build labour is left out**: any line whose product code
 * starts with `RCS.` (e.g. `RCS.Win.NewBuild`) is a service, not a part, so the
 * profit reads as the margin on the parts alone.
 *
 * `buyPriceExGst` and `sellPrice` are per unit; `shipping` is per line (freight
 * charged once). Figures follow the sheet's ex/inc-GST toggle; the EFT uplift
 * is never included — it's a pass-through fee, not profit.
 */

export interface DesktopBuildProfit {
  customerCost: number;
  ourCost: number;
  shipping: number;
  /** customerCost − ourCost − shipping. Negative = sold below cost. */
  profit: number;
  /** Build-labour (RCS.*) lines left out of the figures. */
  excludedLineCount: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;
const num = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

/** A build-labour / service line rather than a part. */
export const isBuildLabourCode = (productCode: string | undefined | null): boolean =>
  (productCode ?? '').trim().toUpperCase().startsWith('RCS.');

export function computeDesktopBuildProfit(
  items: QuoteItem[],
  mode: 'ex' | 'inc' = 'ex',
): DesktopBuildProfit {
  const gst = mode === 'inc' ? 1.1 : 1;
  let customerCost = 0;
  let ourCost = 0;
  let shipping = 0;
  let excludedLineCount = 0;

  for (const item of items) {
    if (!item.markedForInvoice || !item.selectedOptionId) continue;
    const opt = item.options?.find((o) => o.id === item.selectedOptionId);
    if (!opt) continue;
    if (isBuildLabourCode(opt.productCode)) {
      excludedLineCount += 1;
      continue;
    }
    customerCost += optionLineSellDisplay(opt, mode);
    ourCost += optionQty(opt) * num(opt.buyPriceExGst) * gst;
    shipping += num(opt.shipping) * gst;
  }

  customerCost = round2(customerCost);
  ourCost = round2(ourCost);
  shipping = round2(shipping);
  return {
    customerCost,
    ourCost,
    shipping,
    profit: round2(customerCost - ourCost - shipping),
    excludedLineCount,
  };
}
